import { QueryClient, skipToken, useQuery } from "@tanstack/react-query";

import { useStoreKey } from "@/hooks/stores";
import {
  Appointment,
  fetchAppointment,
  fetchStore,
  Store,
} from "@/services/booking";
import { isSetAndNotEmpty } from "@/utils/helpers";

const AppointmentKey = (id?: string) => ["appointments", id];

export function setAppointment(
  appointment: Appointment,
  queryClient: QueryClient,
) {
  queryClient.setQueryData(AppointmentKey(appointment.id.toString()), {
    type: "success",
    appointment,
  });
}

export function useAppointmentQuery(id?: string) {
  return useQuery({
    queryKey: AppointmentKey(id),
    queryFn: id ? () => fetchAppointment(id) : skipToken,
  });
}

type AppointmentAndStoreResult =
  | {
      type: "pending";
    }
  | {
      type: "error";
    }
  | {
      type: "notCustomersAppointment";
    }
  | {
      type: "success";
      appointment: Appointment;
      store: Store;
    };

export const useAppointmentAndStore = (
  id?: string,
): AppointmentAndStoreResult => {
  const appointmentQuery = useAppointmentQuery(id);

  const _appointment =
    appointmentQuery.data?.type === "success"
      ? appointmentQuery.data.appointment
      : null;

  const storeCode = _appointment?.store_code;

  const storeQuery = useQuery({
    queryKey: useStoreKey(storeCode),
    queryFn: isSetAndNotEmpty(storeCode)
      ? () => fetchStore(storeCode)
      : skipToken,
    enabled: !!_appointment,
  });

  if (appointmentQuery.isPending) {
    return {
      type: "pending",
    };
  }

  if (appointmentQuery.isError) {
    return {
      type: "error",
    };
  }

  if (appointmentQuery.data.type === "error") {
    return {
      type: "error",
    };
  }

  if (appointmentQuery.data.type === "notCustomersAppointment") {
    return {
      type: "notCustomersAppointment",
    };
  }

  if (storeQuery.isPending) {
    return {
      type: "pending",
    };
  }

  if (storeQuery.isError) {
    return {
      type: "error",
    };
  }

  return {
    type: "success",
    appointment: appointmentQuery.data.appointment,
    store: storeQuery.data,
  };
};
